import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
	Dialog,
	Grid,
	InputField,
	Select,
	toast,
} from '@components';
import {
	CARGO_ROW_TYPE_LABEL,
	CARGO_TYPE,
	HALF_PALLET_ITEM_TYPE,
	INPUT_TYPE,
	ITEM_TYPE_CARGO_TYPES,
	TItemType,
	TItemTypeCargoType,
	TItemTypeFields,
} from '@types';
import { apiErrorsToValidationErrors, itemTypeMaximumSchema, withPlural } from '@utils';
import { useValidation } from '@hooks';
import { useCreateItemType, useUpdateItemType } from '@api/RateCards/itemTypeApis';
import { DeleteItemTypeButton } from '../DeleteItemTypeButton';

export interface ItemTypeDialogProps {
	open: boolean;
	/** The Item type to edit, or null to add one. */
	itemType: TItemType | null;
	onClose: () => void;
	/** Called once the API has saved it. Until then the dialog stays open. */
	onSaved: () => void;
	/** Called once the API has deleted it. */
	onDeleted: () => void;
}

type TMaximumField = 'max_length_cm' | 'max_width_cm' | 'max_height_cm' | 'max_weight_kg';

// The maxima as typed, so a half-typed number survives until it is saved.
type TForm = Record<TMaximumField, string> & {
	name: string;
	cargo_type: TItemTypeCargoType | '';
}

const formFor = (itemType: TItemTypeFields | null): TForm => ({
	name: itemType?.name ?? '',
	cargo_type: itemType?.cargo_type ?? '',
	max_length_cm: `${itemType?.max_length_cm ?? ''}`,
	max_width_cm: `${itemType?.max_width_cm ?? ''}`,
	max_height_cm: `${itemType?.max_height_cm ?? ''}`,
	max_weight_kg: `${itemType?.max_weight_kg ?? ''}`,
});

const FORM_FIELDS = Object.keys(formFor(null));

const inUseNotice = (laneCount: number) => {
	if (laneCount < 1) return '';
	return `Changes apply to the ${laneCount} ${withPlural('lane', laneCount)} that ${laneCount === 1 ? 'uses' : 'use'} this item type.`;
}

export const ItemTypeDialog = (props: ItemTypeDialogProps) => {
	const {
		open,
		itemType,
		onClose,
		onSaved,
		onDeleted,
	} = props;

	const [form, setForm] = useState<TForm>(formFor(itemType));
	const footprintFixed = form.cargo_type === CARGO_TYPE.PALLETS;

	const { creatingItemType, callCreateItemType } = useCreateItemType();
	const { updatingItemType, callUpdateItemType } = useUpdateItemType();

	/* Only what is missing or not above 0 is caught here. The API checks the decimal places, the
	   upper limits and duplicate names, and its errors land on the same fields. */
	const { connect, formValid, clearErrors, updateBulkValidationErrors } = useValidation({
		name: z.string().trim().min(1, 'Name is required'),
		cargo_type: z.string()
			.min(1, 'Cargo type is required')
			.pipe(z.enum(ITEM_TYPE_CARGO_TYPES, { message: 'Invalid cargo type' })),
		max_length_cm: itemTypeMaximumSchema('Max length'),
		max_width_cm: itemTypeMaximumSchema('Max width'),
		max_height_cm: itemTypeMaximumSchema('Max height'),
		max_weight_kg: itemTypeMaximumSchema('Max weight'),
	});

	const update = (field: keyof TForm, value: string) => {
		setForm((current) => ({
			...current,
			[field]: value
		}));
	}

	const selectCargoType = (cargoType: string) => {
		// Only on switching to Pallet: picking it again would wipe what was typed since.
		if (cargoType === CARGO_TYPE.PALLETS && form.cargo_type !== CARGO_TYPE.PALLETS) {
			setForm(formFor(HALF_PALLET_ITEM_TYPE));
			return;
		}
		update('cargo_type', cargoType);
	}

	const save = () => {
		if (!formValid(form).success) return;
		const payload: TItemTypeFields = {
			name: form.name.trim(),
			cargo_type: form.cargo_type as TItemTypeCargoType,
			max_length_cm: Number(form.max_length_cm),
			max_width_cm: Number(form.max_width_cm),
			max_height_cm: Number(form.max_height_cm),
			max_weight_kg: Number(form.max_weight_kg),
		};
		const request = itemType ?
			callUpdateItemType({ payload, routes: [itemType.id] }) :
			callCreateItemType({ payload });
		request
			.then((res) => {
				// An unreachable server resolves with nothing rather than throwing.
				if (!res?.data) {
					toast.error('Unable to save the item type.');
					return;
				}
				toast.success(itemType ? 'Item type saved' : 'Item type added');
				onSaved();
			})
			.catch((err) => {
				const response = err?.cause?.response;
				const errors = response?.data?.errors;
				if (response?.status === 422 && errors && Object.keys(errors).every((field) => FORM_FIELDS.includes(field))) {
					updateBulkValidationErrors(apiErrorsToValidationErrors(errors));
					return;
				}
				toast.error(response?.data?.message || 'Unable to save the item type.');
			});
	}

	const maximumField = (field: TMaximumField, label: string, disabled = false) => (
		<InputField
			label={label}
			name={field}
			type={INPUT_TYPE.NUMBER}
			value={form[field]}
			{...connect(field)}
			onChange={(e) => update(field, e.target.value)}
			step={'0.01'}
			disabled={disabled}
			required
		/>
	);

	/* The dialog is mounted for the life of the page, so it has to be filled afresh on every
	   opening: from the row being edited, or blank to add one. */
	useEffect(() => {
		if (open) {
			setForm(formFor(itemType));
			clearErrors();
		}
	}, [open]);

	/* Switching to Pallet fills every field, so errors from an earlier save no longer apply. Cleared
	   after the render, as the Select puts the other fields' errors back once it has changed. */
	useEffect(() => {
		if (form.cargo_type === CARGO_TYPE.PALLETS) clearErrors();
	}, [form.cargo_type]);

	return (
		<Dialog
			size={'large'}
			open={open}
			onClose={onClose}
			title={itemType ? 'Edit item type' : 'Add item type'}
			text={inUseNotice(itemType?.lane_count ?? 0)}
			actionButtonText={itemType ? 'Save' : 'Add item type'}
			actionButtonFn={save}
			actionButtonLoading={creatingItemType || updatingItemType}
			closeOnButtonAction={false}
			slot={
				<>
					<Grid minWidth={'0px'}>
						<InputField
							label={'Name'}
							name={'name'}
							value={form.name}
							placeholder={'e.g. Bundle 1.8'}
							{...connect('name')}
							onChange={(e) => update('name', e.target.value)}
							required
						/>
						<Select
							label={'Cargo type'}
							name={'cargo_type'}
							value={form.cargo_type}
							placeholder={'Select a cargo type'}
							{...connect('cargo_type')}
							onChange={selectCargoType}
							required
						>
							{ITEM_TYPE_CARGO_TYPES.map((cargoType) => (
								<Select.Option key={cargoType} value={cargoType}>{CARGO_ROW_TYPE_LABEL[cargoType]}</Select.Option>
							))}
						</Select>
					</Grid>
					<Grid columns={4} minWidth={'0px'}>
						{maximumField('max_length_cm', 'Max length (cm)', footprintFixed)}
						{maximumField('max_width_cm', 'Max width (cm)', footprintFixed)}
						{maximumField('max_height_cm', 'Max height (cm)')}
						{maximumField('max_weight_kg', 'Max weight (kg)')}
					</Grid>
					{itemType && <div>
						<DeleteItemTypeButton itemType={itemType} onDeleted={onDeleted} />
					</div>}
				</>
			}
		/>
	);
}
